/**
 * SC-5 — the shared Plan Review report, read by the building department.
 *
 * No login, no session: the token in the path is the whole credential, exactly
 * as the Spec Sheet's /s/{token} page works. The meta is a whitelist built on
 * the server; the report itself is a PDF from the share's own door.
 */
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '@/styles/spec-sheet.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (value) => {
  if (!value) return '';
  const when = new Date(value);
  return Number.isNaN(when.getTime()) ? '' : when.toLocaleDateString();
};

export default function PublicPlanReview() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [gone, setGone] = useState(false);
  const [pdf, setPdf] = useState(null);
  const [noMark, setNoMark] = useState(false);

  useEffect(() => {
    let live = true;
    let made = null;
    axios.get(`${API}/public/plan-review/${token}/meta`)
      .then(({ data }) => {
        if (!live) return null;
        setMeta(data);
        return axios.get(`${API}/public/plan-review/${token}`, { responseType: 'blob' });
      })
      .then((answer) => {
        if (!live || !answer) return;
        made = URL.createObjectURL(new Blob([answer.data], { type: 'application/pdf' }));
        setPdf(made);
      })
      .catch(() => { if (live) setGone(true); });
    return () => {
      live = false;
      if (made) URL.revokeObjectURL(made);
    };
  }, [token]);

  if (gone) {
    return (
      <div className="ksp ksp--public" data-testid="public-report-gone">
        <div className="ksp__foot">This report is no longer shared.</div>
      </div>
    );
  }
  if (!meta) {
    return (
      <div className="ksp ksp--public" data-testid="public-report-loading">
        <div className="ksp__foot">Loading…</div>
      </div>
    );
  }

  return (
    <div className="ksp ksp--public" data-testid="public-report">
      <div className="ksp__pub">
        {!noMark && (
          <img src={`${API}/public/plan-review/${token}/mark`} alt={meta.firm_name || 'Firm'}
            onError={() => setNoMark(true)} />
        )}
        <span className="nm" data-testid="public-report-firm">{meta.firm_name || ''}</span>
        <span className="nm" style={{ marginLeft: 'auto' }}>Plan Review — Site Record</span>
      </div>
      <div className="ksprv__meta" data-testid="public-report-meta">
        {[meta.project_name, meta.project_code, meta.address].filter(Boolean).join(' · ')}
      </div>
      <div className="ksprv">
        {pdf ? (
          <iframe title="Plan Review report" src={pdf} data-testid="public-report-pdf" />
        ) : (
          <div className="ksp__foot">Making the report…</div>
        )}
      </div>
      <div className="ksp__foot" data-testid="public-report-foot">
        Prepared by {meta.firm_name || 'this firm'}
        {meta.shared_at ? ` · shared ${fmtDate(meta.shared_at)}` : ''}
        {' · '}
        <a href={`${API}/public/plan-review/${token}`} target="_blank" rel="noopener noreferrer"
          data-testid="public-report-open">Open the PDF</a>
      </div>
    </div>
  );
}
